import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Copy, ShieldAlert, Ban } from "lucide-react";

// WS101-ADMIN-01 · superficie administrativa de credenciales del botón de ayuda WS101.
// Consume EXCLUSIVAMENTE las APIs ya existentes (/api/ws101/credenciales) con la sesión
// autenticada vigente: no hay login propio, no se persiste el token y el backend sigue siendo la
// autoridad (require_supervisor = supervisor INTERNO con alcance global).
const CONTRATO = "delivery_receipt_v1";

const fecha = (iso) =>
  iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—";

const corto = (id) => (id ? `${String(id).slice(0, 8)}…` : "—");

export default function CredencialesWs101() {
  const [credenciales, setCredenciales] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [sitioId, setSitioId] = useState("");
  const [networkId, setNetworkId] = useState("");
  const [evidencia, setEvidencia] = useState("");
  const [emitiendo, setEmitiendo] = useState(false);
  const [revocando, setRevocando] = useState(null);
  const [tokenNuevo, setTokenNuevo] = useState(null);   // SOLO en memoria, nunca se persiste
  const [copiado, setCopiado] = useState(false);
  const [sinPermiso, setSinPermiso] = useState(false);

  const cargar = useCallback(() => {
    api.get("/ws101/credenciales")
      .then((r) => { setCredenciales(r.data || []); setSinPermiso(false); })
      .catch((e) => {
        if (e?.response?.status === 403) setSinPermiso(true);
        else toast.error("No se pudieron cargar las credenciales WS101.");
      });
  }, []);

  useEffect(() => {
    cargar();
    api.get("/sitios").then((r) => setSitios(r.data || [])).catch(() => setSitios([]));
  }, [cargar]);

  const emitir = async () => {
    if (!sitioId || !networkId.trim() || !evidencia.trim()) {
      toast.error("Indica el sitio, el identificador de red y la referencia de aprobación.");
      return;
    }
    setEmitiendo(true);
    try {
      const r = await api.post("/ws101/credenciales", {
        sitio_id: sitioId, network_id: networkId.trim(),
        identity_contract: CONTRATO, evidence_ref: evidencia.trim(),
      });
      setTokenNuevo(r.data?.token || "");
      setCopiado(false);
      setNetworkId("");
      setEvidencia("");
      cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo emitir la credencial.");
    } finally {
      setEmitiendo(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(tokenNuevo);
      setCopiado(true);
      toast.success("Credencial copiada. No volverá a mostrarse.");
    } catch {
      setCopiado(true);
      toast.message("Copia manual: selecciona el texto mostrado.");
    }
  };

  const revocar = async (cid) => {
    setRevocando(cid);
    try {
      await api.post(`/ws101/credenciales/${cid}/revocar`);
      toast.success("Credencial revocada.");
      cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo revocar la credencial.");
    } finally {
      setRevocando(null);
    }
  };

  const nombreSitio = (id) => sitios.find((s) => s.id === id)?.nombre || corto(id);

  if (sinPermiso) {
    return (
      <div className="tema-noche-auto" data-testid="ws101-cred-sin-permiso">
        <h1 className="text-2xl font-semibold tracking-tight text-[#012472]">Credenciales WS101</h1>
        <p className="text-sm text-slate-500 mt-2">
          Esta superficie exige un <b>supervisor interno con alcance global</b>. Tu sesión no lo acredita.
        </p>
      </div>
    );
  }

  return (
    <div className="tema-noche-auto space-y-6" data-testid="ws101-credenciales-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#012472] flex items-center gap-2">
          <KeyRound className="h-5 w-5" /> Credenciales WS101
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Credenciales de ingesta del <b>botón de ayuda WS101</b>. La credencial se muestra una sola
          vez: cópiala en ese momento y consérvala fuera de este sistema.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4"
           data-testid="ws101-cred-form">
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Sitio</label>
            <Select value={sitioId} onValueChange={setSitioId}>
              <SelectTrigger data-testid="ws101-cred-sitio" className="mt-1">
                <SelectValue placeholder="Elige el sitio" />
              </SelectTrigger>
              <SelectContent>
                {sitios.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`ws101-cred-sitio-${s.id}`}>
                    {s.nombre || corto(s.id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Identificador de red</label>
            <Input className="mt-1" value={networkId} onChange={(e) => setNetworkId(e.target.value)}
                   placeholder="p. ej. UG63-pasillo-norte"
                   data-testid="ws101-cred-network" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Referencia de aprobación</label>
            <Input className="mt-1" value={evidencia} onChange={(e) => setEvidencia(e.target.value)}
                   placeholder="acta o acuerdo de recepción conservadora"
                   data-testid="ws101-cred-evidencia" />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Contrato de identidad: <b>{CONTRATO}</b> (recepción conservadora, fijado por el sistema).
          </p>
          <Button onClick={emitir} disabled={emitiendo} data-testid="ws101-cred-emitir">
            {emitiendo ? "Emitiendo…" : "Generar credencial"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
           data-testid="ws101-cred-lista">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-medium text-slate-700">
          Credenciales registradas ({credenciales.length})
        </div>
        {credenciales.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="ws101-cred-vacio">
            Todavía no hay credenciales WS101 emitidas.
          </p>
        ) : (
          <>
            {/* Móvil: tarjetas (una tabla ancha desbordaría la pantalla). */}
            <ul className="md:hidden divide-y divide-slate-100" data-testid="ws101-cred-tarjetas">
              {credenciales.map((c) => (
                <li key={c.id} className="p-4 space-y-1" data-testid={`ws101-cred-tarjeta-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-slate-500">{corto(c.id)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.estado === "active"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-600"}`}>
                      {c.estado === "active" ? "Activa" : "Revocada"}
                    </span>
                  </div>
                  <p className="text-sm font-medium break-words">{nombreSitio(c.sitio_id)}</p>
                  <p className="text-xs text-slate-500 break-words">Red: {c.network_id || "—"}</p>
                  <p className="text-xs text-slate-500">Emitida: {fecha(c.creado_en)}</p>
                  <p className="text-xs text-slate-500">Último uso: {fecha(c.ultimo_uso)}</p>
                  {c.estado === "active" && (
                    <Button variant="outline" size="sm" className="mt-2 w-full"
                            disabled={revocando === c.id} onClick={() => revocar(c.id)}
                            data-testid={`ws101-cred-revocar-movil-${c.id}`}>
                      <Ban className="h-3.5 w-3.5 mr-1" />
                      {revocando === c.id ? "Revocando…" : "Revocar"}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-4 py-2">Identificador</th>
                  <th className="px-4 py-2">Sitio</th>
                  <th className="px-4 py-2">Red</th>
                  <th className="px-4 py-2">Estado</th>
                  <th className="px-4 py-2">Emitida</th>
                  <th className="px-4 py-2">Último uso</th>
                  <th className="px-4 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {credenciales.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100"
                      data-testid={`ws101-cred-fila-${c.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{corto(c.id)}</td>
                    <td className="px-4 py-2">{nombreSitio(c.sitio_id)}</td>
                    <td className="px-4 py-2">{c.network_id || "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.estado === "active"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"}`}
                        data-testid={`ws101-cred-estado-${c.id}`}>
                        {c.estado === "active" ? "Activa" : "Revocada"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{fecha(c.creado_en)}</td>
                    <td className="px-4 py-2 text-slate-600">{fecha(c.ultimo_uso)}</td>
                    <td className="px-4 py-2 text-right">
                      {c.estado === "active" ? (
                        <Button variant="outline" size="sm" disabled={revocando === c.id}
                                onClick={() => revocar(c.id)}
                                data-testid={`ws101-cred-revocar-${c.id}`}>
                          <Ban className="h-3.5 w-3.5 mr-1" />
                          {revocando === c.id ? "Revocando…" : "Revocar"}
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <Dialog open={!!tokenNuevo} onOpenChange={(o) => { if (!o) setTokenNuevo(null); }}>
        <DialogContent data-testid="ws101-cred-token-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Credencial emitida
            </DialogTitle>
            <DialogDescription>
              Se muestra <b>una sola vez</b>. Al cerrar esta ventana ya no podrá recuperarse: si se
              pierde, revoca esta credencial y emite otra.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-900 text-slate-100 p-3 font-mono text-xs break-all"
               data-testid="ws101-cred-token-valor">
            {tokenNuevo}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={copiar} disabled={copiado}
                    data-testid="ws101-cred-copiar">
              <Copy className="h-4 w-4 mr-1" />
              {copiado ? "Copiada" : "Copiar credencial"}
            </Button>
            <Button onClick={() => setTokenNuevo(null)} data-testid="ws101-cred-cerrar-token">
              Ya la guardé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
